import React, {useEffect, useState} from 'react';

import classNames from 'classnames';
import {isEmpty} from 'lodash';
import {Button, Container, Modal, ModalBody, ModalHeader} from 'reactstrap';
import {useParams} from 'react-router';

import ContentTable from 'components/Contents/ContentTable';
import {ContentPaginationState} from 'components/Contents';
import StatusBadge from 'components/Badges/StatusBadge';
import {SearchLoadMore} from 'components/Searches';
import {ImageFlags} from 'components/Images';
import {CB_ACCREDITATION_COLORS} from 'configs';
import {date} from 'utils/dates';

import useGetEconomy from './useGetEconomy';
import useGetCBStandardsEconomy from './useGetCBStandardsEconomy';

import styles from '../styles.module.scss';

const ScopeModal = (props) => {
  const {abId, cbId, country, toggle} = props;
  const {countries_id, country_code, country_name} = country || {};

  const {isLoading, data, meta, reload} = useGetCBStandardsEconomy(
    cbId,
    abId,
    countries_id
  );
  const {total_pages} = meta || {};

  const [offset, setOffset] = useState(0);

  useEffect(() => {
    if (!!country) reload({offset: offset});
  }, [country, offset]);

  return (
    <Modal centered isOpen={!!country} toggle={toggle} size="lg">
      <ModalHeader
        toggle={toggle}
        className="d-flex align-items-center notranslate"
      >
        {country_name}
        {country_code && (
          <ImageFlags countryCode={country_code} className="ml-2" />
        )}
      </ModalHeader>
      <ModalBody style={{padding: '1.5rem 1.5rem 0.5rem 1.5rem'}}>
        <Container className={styles['table-container']}>
          <ContentTable
            v2
            meta={meta}
            hover={false}
            className="mb-0"
            minHeight="364px"
            responsive={false}
            isFetching={isLoading}
            head={[
              'Standards and Schemes',
              'Accreditation Status',
              {
                label: 'Date',
                value: 'suspended_withdrawn_date',
                tooltip:
                  'Country / Economy effectivity date of suspension / withdrawal',
                onChangeSort: (order) => {
                  setOffset(0);
                  reload({
                    offset: 0,
                    order: order,
                    order_by: !!order ? 'suspended_withdrawn_date' : '',
                  });
                },
              },
            ]}
          >
            {data.map((item, index) => {
              const {
                country_accreditation_status,
                standards_name,
                scheme_name,
                suspended_withdrawn_date,
              } = item;

              return (
                <tr key={index}>
                  <td>{[standards_name, scheme_name].join(' : ')}</td>
                  <td>
                    <StatusBadge
                      pill
                      status={country_accreditation_status}
                      colors={CB_ACCREDITATION_COLORS}
                      className="text-white"
                    />
                  </td>
                  <td>{date(suspended_withdrawn_date)}</td>
                </tr>
              );
            })}
          </ContentTable>
        </Container>
        <ContentPaginationState
          previousLabel="Previous"
          nextLabel="Next"
          offset={offset}
          totalPages={total_pages ? total_pages : 0}
          handleChangeOffset={(newOffset) => setOffset(newOffset)}
        />
      </ModalBody>
    </Modal>
  );
};

function CBAccreditationScopeEconomy(props) {
  const {selectedAB} = props;

  const {cbId} = useParams();

  const [country, setCountry] = useState('');

  const toggle = (item) => {
    const isOpen = !item.target;
    if (isOpen) setCountry(item);
    else setCountry('');
  };

  const {accreditation_bodies_id} = selectedAB || {};
  const {isLoading, data, meta, reload} = useGetEconomy(
    cbId,
    accreditation_bodies_id
  );
  const {offset, total_pages, total_rows} = meta || {};

  return (
    <Container className="p-0" tag="section">
      <h3 className={classNames(styles['section__title'], 'mb-4')}>
        Country / Economy {`(${total_rows || 0})`}
      </h3>

      <Container className={styles['table-container']}>
        <ContentTable
          v2
          meta={meta}
          hover={false}
          className="mb-0"
          minHeight="364px"
          responsive={false}
          isFetching={isEmpty(data) && isLoading}
          head={['Country / Economy', 'View Full Accreditation Scope']}
          loadMore={
            !isEmpty(data) && (
              <SearchLoadMore
                className={'my-3'}
                isLoading={isLoading}
                loadMore={() => reload(parseInt(offset) + 1)}
                hasMore={parseInt(offset) + 1 < parseInt(total_pages)}
              />
            )
          }
        >
          {data.map((item, index) => {
            const {country_name, country_code, standard_scheme_count} = item;

            return (
              <tr key={index}>
                <td className="d-flex align-items-center">
                  {country_code && (
                    <ImageFlags countryCode={country_code} className="mr-2" />
                  )}
                  {country_name}
                </td>
                {parseInt(standard_scheme_count) > 0 && (
                  <td>
                    <Button
                      outline
                      color="primary"
                      className="rounded"
                      onClick={() => toggle(item)}
                      disabled={isLoading}
                    >
                      View
                    </Button>
                  </td>
                )}
              </tr>
            );
          })}
        </ContentTable>
      </Container>

      <ScopeModal
        abId={accreditation_bodies_id}
        cbId={cbId}
        country={country}
        toggle={toggle}
      />
    </Container>
  );
}

export default CBAccreditationScopeEconomy;
